<template>
  <div class="project-notes">
    <div class="block block-rounded mb-4">
      <div class="block-header border-bottom">
        <h3 class="block-title">
          Add Note
        </h3>
      </div>
      <div class="block-content">
        <fieldset :disabled="submitting">
          <div class="form-group">
            <label for="project-note-content">Note</label>
            <textarea
              id="project-note-content"
              v-model.trim="newNoteContent"
              class="form-control"
              rows="4"
              placeholder="Write an internal project note"
            />
            <small class="form-text text-muted">
              Notes can include up to 5 files. Allowed formats: PDF, DOC, DOCX, TXT. Max 20MB each.
            </small>
          </div>

          <div class="form-group">
            <label for="project-note-files">Attachments</label>
            <div class="custom-file">
              <input
                id="project-note-files"
                ref="fileInput"
                type="file"
                class="custom-file-input"
                multiple
                accept=".pdf,.doc,.docx,.txt"
                @change="onFilesSelected"
              >
              <label
                class="custom-file-label"
                for="project-note-files"
              >Choose files</label>
            </div>
          </div>

          <div
            v-if="newNoteFiles.length"
            class="mb-3"
          >
            <div
              v-for="(file, index) in newNoteFiles"
              :key="file.name + '-' + index"
              class="project-notes-file-chip d-flex align-items-center justify-content-between"
            >
              <div class="d-flex align-items-center text-break me-3">
                <i :class="['fa', fileIcon(file.type), 'text-muted', 'me-2']" />
                <span>{{ file.name }}</span>
                <small class="text-muted ms-2">({{ formatFileSize(file.size) }})</small>
              </div>
              <button
                type="button"
                class="btn btn-sm btn-alt-danger"
                aria-label="Remove selected file"
                @click="removeSelectedFile(index)"
              >
                <i class="fa fa-times" />
              </button>
            </div>
          </div>

          <div class="d-flex flex-wrap align-items-center pb-4">
            <b-button
              variant="success"
              size="sm"
              class="mr-2 mb-2"
              :disabled="submitting || !newNoteContent"
              @click="createNote"
            >
              <b-spinner
                v-if="submitting"
                small
                class="me-1"
              />
              Save Note
            </b-button>
            <b-button
              variant="secondary"
              size="sm"
              class="mb-2"
              :disabled="submitting"
              @click="resetCreateForm"
            >
              Clear
            </b-button>
          </div>
        </fieldset>
      </div>
    </div>

    <div class="block block-rounded">
      <div class="block-header border-bottom">
        <h3 class="block-title">
          Notes
        </h3>
      </div>
      <div class="block-content">
        <div
          v-if="loading"
          class="py-5 text-center"
        >
          <b-spinner
            variant="primary"
            label="Loading notes"
          />
        </div>

        <div
          v-else-if="!notes.length"
          class="project-notes-empty-state text-center py-5"
        >
          <div class="project-notes-empty-icon mx-auto mb-3">
            <i class="far fa-sticky-note" />
          </div>
          <h4 class="h5 mb-2">
            No notes yet
          </h4>
          <p class="text-muted mb-0">
            Use the form above to capture project updates, assumptions, and internal context.
          </p>
        </div>

        <div
          v-else
          id="project-notes-list"
        >
          <project-note-item
            v-for="note in notes"
            :key="note.id"
            :note="note"
            :auth-user-id="authUserId"
            :is-super-admin="isSuperAdmin"
            :is-editing="editingNoteId === note.id"
            :saving-edit="savingEdit"
            :value="editingNoteId === note.id ? editContent : ''"
            @input="editContent = $event"
            @start-edit="startEdit"
            @save-edit="saveEdit"
            @cancel-edit="cancelEdit"
            @delete-note="confirmDelete"
            @download-file="downloadFile"
            @delete-file="confirmDeleteFile"
          />

          <div class="d-flex justify-content-center pt-2 pb-4">
            <b-pagination
              v-show="pagination.totalRows > pagination.perPage"
              v-model="pagination.currentPage"
              :total-rows="pagination.totalRows"
              :per-page="pagination.perPage"
              size="sm"
              aria-controls="project-notes-list"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import permissions from "../../../mixins/permissions";
import ProjectNoteItem from "./ProjectNoteItem";

const allowedExtensions = ["pdf", "doc", "docx", "txt"];
const maxFileSize = 20 * 1024 * 1024;
const maxFiles = 5;

export default {
    name: "ProjectNotes",
    components: {
        ProjectNoteItem,
    },
    mixins: [permissions],
    props: {
        projectId: {
            type: Number,
            required: true,
        },
        authUserId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            notes: [],
            loading: false,
            submitting: false,
            savingEdit: false,
            newNoteContent: "",
            newNoteFiles: [],
            editingNoteId: null,
            editContent: "",
            pagination: {
                currentPage: 1,
                totalRows: 0,
                perPage: 3,
            },
        };
    },
    watch: {
        "pagination.currentPage": function () {
            this.fetchNotes();
        },
    },
    mounted() {
        this.fetchNotes();
    },
    methods: {
        notify(type, message) {
            One.helpers("jq-notify", {
                type,
                icon: type === "success" ? "fa fa-check me-1" : "fa fa-times me-1",
                message,
            });
        },
        async fetchNotes() {
            this.loading = true;

            try {
                const response = await axios.get(`/admin/project/${this.projectId}/notes/list`, {
                    params: {
                        page: this.pagination.currentPage,
                        per_page: this.pagination.perPage,
                    },
                });

                this.notes = response.data.data || [];
                this.pagination.totalRows = response.data.meta ? response.data.meta.total : 0;
            } catch (error) {
                this.notify("danger", "Unable to load project notes.");
            } finally {
                this.loading = false;
            }
        },
        onFilesSelected(event) {
            const selectedFiles = Array.from(event.target.files || []);
            const combinedFiles = [...this.newNoteFiles];

            for (const file of selectedFiles) {
                if (combinedFiles.length >= maxFiles) {
                    this.notify("danger", `You can attach up to ${maxFiles} files per note.`);
                    break;
                }

                const extension = file.name.split(".").pop().toLowerCase();

                if (!allowedExtensions.includes(extension)) {
                    this.notify("danger", `${file.name} is not a supported file type.`);
                    continue;
                }

                if (file.size > maxFileSize) {
                    this.notify("danger", `${file.name} exceeds the 20MB limit.`);
                    continue;
                }

                combinedFiles.push(file);
            }

            this.newNoteFiles = combinedFiles;
            this.syncFileInputLabel();
        },
        removeSelectedFile(index) {
            this.newNoteFiles.splice(index, 1);
            this.syncFileInputLabel();
        },
        syncFileInputLabel() {
            if (!this.$refs.fileInput) {
                return;
            }

            const label = this.$refs.fileInput.nextElementSibling;

            if (label) {
                label.textContent = this.newNoteFiles.length
                    ? `${this.newNoteFiles.length} file${this.newNoteFiles.length > 1 ? "s" : ""} selected`
                    : "Choose files";
            }
        },
        resetCreateForm() {
            this.newNoteContent = "";
            this.newNoteFiles = [];

            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = null;
            }

            this.syncFileInputLabel();
        },
        async createNote() {
            if (!this.newNoteContent) {
                this.notify("danger", "Please enter note content.");
                return;
            }

            this.submitting = true;

            try {
                const formData = new FormData();
                formData.append("content", this.newNoteContent);

                this.newNoteFiles.forEach((file) => {
                    formData.append("files[]", file);
                });

                await axios.post(`/admin/project/${this.projectId}/notes`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });

                if (this.pagination.currentPage !== 1) {
                    this.pagination.currentPage = 1;
                } else {
                    await this.fetchNotes();
                }
                this.resetCreateForm();
                this.notify("success", "Note created successfully.");
            } catch (error) {
                this.notify("danger", "Unable to create the note.");
            } finally {
                this.submitting = false;
            }
        },
        startEdit(note) {
            this.editingNoteId = note.id;
            this.editContent = note.content;
        },
        cancelEdit() {
            this.editingNoteId = null;
            this.editContent = "";
        },
        async saveEdit(noteId) {
            if (!this.editContent) {
                this.notify("danger", "Please enter note content.");
                return;
            }

            this.savingEdit = true;

            try {
                await axios.put(`/admin/project/notes/${noteId}`, {
                    content: this.editContent,
                });
                await this.fetchNotes();
                this.cancelEdit();
                this.notify("success", "Note updated successfully.");
            } catch (error) {
                this.notify("danger", "Unable to update the note.");
            } finally {
                this.savingEdit = false;
            }
        },
        async confirmDelete(noteId) {
            try {
                const confirmed = await this.$bvModal.msgBoxConfirm("Are you sure you want to delete this note?");

                if (confirmed) {
                    await axios.delete(`/admin/project/notes/${noteId}`);
                    await this.fetchNotes();
                    this.notify("success", "Note deleted successfully.");
                }
            } catch (error) {
                this.notify("danger", "Unable to delete the note.");
            }
        },
        async confirmDeleteFile(fileId) {
            try {
                const confirmed = await this.$bvModal.msgBoxConfirm("Are you sure you want to delete this file?");

                if (confirmed) {
                    await axios.delete(`/admin/project/notes/file/${fileId}`);
                    await this.fetchNotes();
                    this.notify("success", "File deleted successfully.");
                }
            } catch (error) {
                this.notify("danger", "Unable to delete the file.");
            }
        },
        async downloadFile(fileId) {
            try {
                const response = await axios.get(`/admin/project/notes/file/${fileId}/download`);
                const url = response.data && response.data.url ? response.data.url : null;

                if (!url) {
                    this.notify("danger", "Unable to download the file.");
                    return;
                }

                window.open(url, "_blank");
            } catch (error) {
                this.notify("danger", "Unable to download the file.");
            }
        },
        formatFileSize(bytes) {
            if (!bytes) {
                return "0 B";
            }

            const units = ["B", "KB", "MB", "GB"];
            const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
            const size = bytes / Math.pow(1024, exponent);

            return `${size.toFixed(size >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
        },
        fileIcon(mimeType = "") {
            if (mimeType.includes("pdf")) {
                return "fa-file-pdf";
            }

            if (mimeType.includes("word") || mimeType.includes("document")) {
                return "fa-file-word";
            }

            if (mimeType.includes("text")) {
                return "fa-file-alt";
            }

            return "fa-file";
        },
    },
};
</script>

<style scoped>
.project-notes-file-chip {
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    background: #f8f9fa;
}

.project-notes-file-chip + .project-notes-file-chip {
    margin-top: 0.75rem;
}

.project-notes-empty-state {
    max-width: 34rem;
    margin: 0 auto;
}

.project-notes-empty-icon {
    width: 4rem;
    height: 4rem;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f6f9;
    color: #5c80d1;
    font-size: 1.25rem;
}

@media (prefers-reduced-motion: reduce) {
    .project-notes-file-chip {
        transition: none;
    }
}
</style>
